import { createContext, ReactNode, useReducer } from 'react';
import { SocketMarketplaceWorkerNotificationModel } from '../../components/smartsend/SmartSendSocketsModels';
import { ConcernModel } from '../../components/concerns/Concern';

export interface AccountModel {
  Id: string;
  SourceId: string;
  SourceName: string;
  Description: string;
  Links: { [key: string]: string };
}

export interface ConcernSummary {
  Concerns: ConcernModel[];
  Sockets: SocketMarketplaceWorkerNotificationModel[];
  Accounts: AccountModel[];
}

export interface IConcernsContextAction {
  type: 'ADD-CONCERN' | 'ADD-SOCKET' | 'INITIALISE';
  payload?: any;
}

export function ConcernsContextReducer(state: ConcernSummary, action: IConcernsContextAction) {
  switch (action.type) {
    case 'ADD-CONCERN': {
      const updated = { ...state };
      if (action.payload?.Type) {
        updated.Concerns = updated.Concerns.filter(
          (x) =>
            x.Type !== action.payload.Type &&
            x.Title !== action.payload.Title &&
            x.Description !== action.payload.Description
        );
        updated.Concerns = [action.payload, ...updated.Concerns];
      }
      return updated;
    }

    case 'ADD-SOCKET': {
      const newSocket = action.payload as SocketMarketplaceWorkerNotificationModel;
      const updated = state.Sockets.filter(
        (x) =>
          // Remove older sockets for the same account and type.
          !(
            x.Details.MPAccountId === newSocket.Details.MPAccountId &&
            x.Details.Stage === newSocket.Details.Stage &&
            x.Details.MarketplaceSource == newSocket.Details.MarketplaceSource &&
            x.Details.SentTime < newSocket.Details.SentTime
          )
      );
      return { ...state, Sockets: [action.payload, ...updated] };
    }

    case 'INITIALISE':
      return { ...state, Accounts: action.payload.accounts, Concerns: [...state.Concerns, ...action.payload.concerns] };

    default:
      return state;
  }
}

const initialState: ConcernSummary = { Concerns: [], Accounts: [], Sockets: [] };

const ConcernsContext = createContext<{
  state: typeof initialState;
  dispatch: (action: IConcernsContextAction) => void;
}>({
  state: initialState,
  dispatch: () => {}
});

const ConcernsContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(ConcernsContextReducer, initialState);
  const value = { state, dispatch };
  return <ConcernsContext.Provider value={value}>{children}</ConcernsContext.Provider>;
};

const ContextConsumer = ConcernsContext.Consumer;

export { ConcernsContext, ConcernsContextProvider, ContextConsumer };
